import React, { createContext, useContext, useState } from 'react'
import isNode from 'detect-node'
import bugsnagClient from 'services/bugsnag-client'
import { useRoktEnabled } from 'utils/featureFlag'

export const RoktLauncherContext = createContext(null)

export function useRoktLauncher() {
  return useContext(RoktLauncherContext)
}

export function RoktLauncherContextProvider({
  children,
  accountId,
  sandbox = false,
}) {
  const roktEnabled = useRoktEnabled()
  const [launcher, setLauncher] = useState(null)

  // Do not use hook on node, or when the flag is off
  if (isNode || !roktEnabled) {
    return (
      <RoktLauncherContext.Provider>{children}</RoktLauncherContext.Provider>
    )
  }

  if (!launcher) {
    const target = document.head || document.body
    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = 'https://apps.rokt.com/wsdk/integrations/launcher.js'
    script.fetchpriority = 'high'
    script.crossOrigin = 'anonymous'
    script.async = true

    target.appendChild(script)

    const launcherPromise = new Promise((resolve, reject) => {
      script.onload = () => {
        try {
          const launcherCreated = window.Rokt.createLauncher({
            accountId,
            sandbox,
          })
          resolve(launcherCreated)
        } catch (err) {
          reject(err)
        }
      }
      script.onerror = () => {
        reject(new Error('Failed to load Rokt launcher script'))
      }
    })

    // This promise is created on every page (see App.js), but only ever
    // consumed on the checkout/order-status page via useRoktLauncher().
    // Without a handler attached here, a rejection on any other page is
    // a guaranteed unhandled promise rejection.
    launcherPromise.catch(err => {
      if (bugsnagClient) {
        bugsnagClient.notify(err, event => {
          event.severity = 'info'
        })
      }
    })

    setLauncher(launcherPromise)
  }
  // Return the context provider
  return (
    <RoktLauncherContext.Provider value={launcher}>
      {children}
    </RoktLauncherContext.Provider>
  )
}
